import {
  Asset,
  BucketDefinition,
  EditorConfiguration,
  EditorValidationErrors,
  LineAsset,
  LineDefinition,
  LineTestDefinition,
  LocaleAsset,
  LocaleDefinition, OptionListDefinition,
  PriorEnquiryDefinition,
  QuestionAsset,
  QuestionDefinition,
  TableDefinition, WrapUpLine,
} from '../../../dtos/editor.dto';
import { PriorEnquiryChannel } from '../../enquiry/enquiry.service';
import {
  addMocks, APIRequest, formatRequestParams, Paginated,
} from '../../types';
import Mocks from './mocks';
import { EditorAPI, TypeAheadBucketValueReplacements } from './types';
import { axiosAuthClient } from '../../auth/axiosAuthClient';
import { getFile } from '../../../utils/getFile';

const defaultUrl = '/apiRes';
const v1Path = '/v1';
const v2Path = '/v2';

export const getEditorAPI = (baseUrl = defaultUrl) => {
  const defaultV1Url = `${baseUrl}${v1Path}`;
  const defaultV2Url = `${baseUrl}${v2Path}`;

  const api: EditorAPI = {
    getBranchInfo: new APIRequest(
      (branchName) => axiosAuthClient.get<string[]>(`${defaultV1Url}/branches/${encodeURIComponent(branchName)}`).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+', method: 'get' },
    ),
    getLinesList: new APIRequest(
      ({ branchName, tagName }) => axiosAuthClient.get<LineAsset[]>(`${defaultV1Url}/branches/${branchName}/tree/lines${tagName ? `?tagName=${tagName}` : ''}`).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/lines', method: 'get' },
    ),
    getBucketsList: new APIRequest(
      ({ branchName, tagName }) => axiosAuthClient.get<Asset[]>(`${defaultV1Url}/branches/${branchName}/tree/buckets${tagName ? `?tagName=${tagName}` : ''}`).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/buckets', method: 'get' },
    ),
    getOptionListList: new APIRequest(
      ({ branchName, tagName }) => axiosAuthClient.get<Asset[]>(`${defaultV1Url}/branches/${branchName}/tree/option-lists${tagName ? `?tagName=${tagName}` : ''}`).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/option-lists', method: 'get' },
    ),
    getQuestionsList: new APIRequest(
      ({ branchName, tagName }) => axiosAuthClient.get<QuestionAsset[]>(`${defaultV1Url}/branches/${branchName}/tree/questions${tagName ? `?tagName=${tagName}` : ''}`).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/questions', method: 'get' },
    ),
    getLocalesList: new APIRequest(
      ({ branchName, tagName, includeDefaultLocale }) => axiosAuthClient.get<LocaleAsset[]>(`${defaultV1Url}/branches/${branchName}/tree/locales?includeDefaultLocale=${includeDefaultLocale}${tagName ? `&tagName=${tagName}` : ''}`).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/locales', method: 'get' },
    ),
    getPriorEnquiryChannelList: new APIRequest(
      ({ branchName, tagName }) => axiosAuthClient.get<Asset[]>(`${defaultV1Url}/branches/${branchName}/tree/channels${tagName ? `?tagName=${tagName}` : ''}`).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/prior-enquiry-channels', method: 'get' },
    ),
    getBucket: new APIRequest(
      ({ branchName, tagName, bucketName }) => axiosAuthClient.get<BucketDefinition>(`${defaultV1Url}/branches/${branchName}/rule/buckets/${bucketName}${tagName ? `?tagName=${tagName}` : ''}`).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/buckets\\/[a-zA-Z0-9\\-]+', method: 'get' },
    ),
    updateBucket: new APIRequest(
      ({
        branchName,
        bucketName,
        bucket,
        commitMessage,
      }) => axiosAuthClient.put<BucketDefinition>(`${defaultV1Url}/branches/${branchName}/rule/buckets/${bucketName}`, { commitMessage, asset: bucket }).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/buckets\\/[a-zA-Z0-9\\-]+', method: 'put' },
    ),
    createBucket: new APIRequest(
      ({ branchName, bucket, commitMessage }) => axiosAuthClient.post<BucketDefinition>(`${defaultV1Url}/branches/${branchName}/rule/buckets`, { commitMessage, asset: bucket }).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/buckets', method: 'post' },
    ),
    deleteBucket: new APIRequest(
      ({ branchName, bucketName }) => axiosAuthClient.delete<void>(`${defaultV1Url}/branches/${branchName}/rule/buckets/${bucketName}`).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/buckets\\/[a-zA-Z0-9\\-]+', method: 'delete' },
    ),
    getLocale: new APIRequest(
      ({ branchName, tagName, localeName }) => axiosAuthClient.get<LocaleDefinition>(`${defaultV1Url}/branches/${branchName}/rule/locales/${localeName}${tagName ? `?tagName=${tagName}` : ''}`).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/rule\\/locales\\/[a-zA-Z0-9\\-]+', method: 'get' },
    ),
    updateLocale: new APIRequest(
      ({
        branchName, localeName, locale, commitMessage,
      }) => axiosAuthClient.put<LocaleDefinition>(`${defaultV1Url}/branches/${branchName}/rule/locales/${localeName}`, { commitMessage, asset: locale }).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/rule\\/locales\\/[a-zA-Z0-9\\-]+', method: 'put' },
    ),
    createLocale: new APIRequest(
      ({ branchName, locale, commitMessage }) => axiosAuthClient.post<LocaleDefinition>(`${defaultV1Url}/branches/${branchName}/rule/locales`, { commitMessage, asset: locale }).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/rule\\/locales', method: 'post' },
    ),
    deleteLocale: new APIRequest(
      ({ branchName, localeName }) => axiosAuthClient.delete<void>(`${defaultV1Url}/branches/${branchName}/locales/${localeName}`).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/locales\\/[a-zA-Z0-9\\-]+', method: 'delete' },
    ),
    getQuestion: new APIRequest(
      ({ branchName, tagName, questionName }) => axiosAuthClient.get<QuestionDefinition>(`${defaultV1Url}/branches/${branchName}/rule/questions/${questionName}${tagName ? `?tagName=${tagName}` : ''}`).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/rule\\/questions\\/[a-zA-Z0-9\\-]+', method: 'get' },
    ),
    searchQuestions: new APIRequest(
      ({ branchName, tagName, questionNames }) => axiosAuthClient.post<QuestionDefinition[]>(`${defaultV1Url}/branches/${branchName}/rule/questions/searches${tagName ? `?tagName=${tagName}` : ''}`, { names: questionNames }).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/rule\\/questions\\/searches', method: 'post' },
    ),
    updateQuestion: new APIRequest(
      ({
        branchName, questionName, question, commitMessage,
      }) => axiosAuthClient.put<QuestionDefinition>(`${defaultV1Url}/branches/${branchName}/rule/questions/${questionName}`, { commitMessage, asset: question }).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/rule\\/questions\\/[a-zA-Z0-9\\-]+', method: 'put' },
    ),
    createQuestion: new APIRequest(
      ({ branchName, question, commitMessage }) => axiosAuthClient.post<QuestionDefinition>(`${defaultV1Url}/branches/${branchName}/rule/questions`, { commitMessage, asset: question }).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/rule\\/questions', method: 'post' },
    ),
    deleteQuestion: new APIRequest(
      ({ branchName, questionName }) => axiosAuthClient.delete<void>(`${defaultV1Url}/branches/${branchName}/rule/questions/${questionName}`).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/rule\\/questions\\/[a-zA-Z0-9\\-]+', method: 'delete' },
    ),
    getOptionList: new APIRequest(
      ({ branchName, tagName, optionListName }) => axiosAuthClient.get<OptionListDefinition>(`${defaultV1Url}/branches/${branchName}/rule/option-lists/${optionListName}${tagName ? `?tagName=${tagName}` : ''}`).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/rule/option-lists\\/[a-zA-Z0-9\\-]+', method: 'get' },
    ),
    getOptionListV2: new APIRequest(
      ({ branchName, tagName, optionListName }) => axiosAuthClient.get<OptionListDefinition>(`${defaultV2Url}/branches/${branchName}/rule/option-lists/${optionListName}${tagName ? `?tagName=${tagName}` : ''}`).then(({ data }) => data),
      { urlRegexp: '\\/api\\/\\/branches\\/[a-zA-Z0-9\\-]+\\/rule\\/option-lists\\/[a-zA-Z0-9\\-]+\\', method: 'get' },
    ),
    getOptionListOptions: new APIRequest(
      (params) => axiosAuthClient.get<Paginated<OptionListDefinition>>(`${defaultV1Url}/branches/${params.branchName}/rule/option-lists/${params.optionListName}/options?containsIgnoreCase=${params.filterText}${params.tagName ? `&tagName=${params.tagName}` : ''}`, { params: formatRequestParams(params.params) }).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/rule/option-lists\\/[a-zA-Z0-9\\-]+\\/options', method: 'get' },
    ),
    updateOptionList: new APIRequest(
      ({
        branchName, optionListName, optionList, commitMessage,
      }) => axiosAuthClient.put<OptionListDefinition>(`${defaultV2Url}/branches/${branchName}/rule/option-lists/${optionListName}`, { commitMessage, asset: optionList }).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/rule/option-lists\\/[a-zA-Z0-9\\-]+', method: 'put' },
    ),
    createOptionList: new APIRequest(
      ({ branchName, optionList, commitMessage }) => axiosAuthClient.post<OptionListDefinition>(`${defaultV1Url}/branches/${branchName}/rule/option-lists`, { commitMessage, asset: optionList }).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/rule/option-lists', method: 'post' },
    ),
    deleteOptionList: new APIRequest(
      ({ branchName, optionListName }) => axiosAuthClient.delete<void>(`${defaultV1Url}/branches/${branchName}/rule/option-lists/${optionListName}`).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/rule/option-lists\\/[a-zA-Z0-9\\-]+', method: 'delete' },
    ),
    importOptionList: new APIRequest(
      ({ branchName, optionListName, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return axiosAuthClient.put(`${defaultV1Url}/branches/${branchName}/rule/option-lists/${optionListName}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }).then((response) => {
          const { location } = response.headers;
          const optionName = location ? location.substring(location.lastIndexOf('/') + 1) : optionListName;
          return { name: optionName };
        });
      },
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/rule/option-lists\\/[a-zA-Z0-9\\-]+', method: 'put' },
    ),
    exportOptionList: new APIRequest(
      ({ branchName, tagName, optionListName }) => axiosAuthClient.get(`${defaultV1Url}/branches/${branchName}/rule/option-lists/${optionListName}${tagName ? `?tagName=${tagName}` : ''}`, {
        responseType: 'blob',
        headers: {
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
      }).then(getFile),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/rule/option-lists\\/[a-zA-Z0-9\\-]+', method: 'get' },
    ),
    getPriorEnquiry: new APIRequest(
      ({ branchName, tagName, priorEnquiryName }) => axiosAuthClient.get<PriorEnquiryDefinition>(`${defaultV1Url}/branches/${branchName}/rule/prior-enquiry-channels/${priorEnquiryName}${tagName ? `?tagName=${tagName}` : ''}`).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/prior-enquiry-channels\\/[a-zA-Z0-9\\-]+', method: 'get' },
    ),
    updatePriorEnquiry: new APIRequest(
      ({
        branchName, priorEnquiryName, priorEnquiry, commitMessage,
      }) => axiosAuthClient.put<PriorEnquiryChannel>(`${defaultV1Url}/branches/${branchName}/rule/prior-enquiry-channels/${priorEnquiryName}`, { commitMessage, asset: priorEnquiry }).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/prior-enquiry-channels\\/[a-zA-Z0-9\\-]+', method: 'put' },
    ),
    createPriorEnquiry: new APIRequest(
      ({ branchName, priorEnquiry, commitMessage }) => axiosAuthClient.post<PriorEnquiryDefinition>(`${defaultV1Url}/branches/${branchName}/rule/prior-enquiry-channels`, { commitMessage, asset: priorEnquiry }).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/prior-enquiry-channels', method: 'post' },
    ),
    deletePriorEnquiry: new APIRequest(
      ({ branchName, priorEnquiryName }) => axiosAuthClient.delete<void>(`${defaultV1Url}/branches/${branchName}/rule/prior-enquiry-channels/${priorEnquiryName}`).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/prior-enquiry-channels\\/[a-zA-Z0-9\\-]+', method: 'delete' },
    ),
    getLine: new APIRequest(
      ({ branchName, tagName, lineName }) => axiosAuthClient.get<LineDefinition>(`${defaultV1Url}/branches/${branchName}/rule/lines/${lineName}${tagName ? `?tagName=${tagName}` : ''}`).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/lines\\/[a-zA-Z0-9\\-]+', method: 'get' },
    ),
    updateLine: new APIRequest(
      ({
        branchName, lineName, line, commitMessage,
      }) => axiosAuthClient.put<LineDefinition>(`${defaultV1Url}/branches/${branchName}/rule/lines/${lineName}`, { commitMessage, asset: line }).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/lines\\/[a-zA-Z0-9\\-]+', method: 'put' },
    ),
    createLine: new APIRequest(
      ({ branchName, line, commitMessage }) => axiosAuthClient.post<LineDefinition>(`${defaultV1Url}/branches/${branchName}/rule/lines`, { commitMessage, asset: line }).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/lines', method: 'post' },
    ),
    deleteLine: new APIRequest(
      ({ branchName, lineName }) => axiosAuthClient.delete<void>(`${defaultV1Url}/branches/${branchName}/rule/lines/${lineName}`).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/rule\\/lines\\/[a-zA-Z0-9\\-]+', method: 'delete' },
    ),
    getWrapUpLinesOrder: new APIRequest(
      ({ branchName, tagName }) => axiosAuthClient.get<WrapUpLine[]>(`${defaultV1Url}/branches/${branchName}/rule/wrapUpLineOrder${tagName ? `?tagName=${tagName}` : ''}`).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/lines\\wrapUpLineOrder', method: 'get' },
    ),
    updateWrapUpLinesOrder: new APIRequest(
      ({ branchName, order, commitMessage }) => axiosAuthClient.put<WrapUpLine[]>(`${defaultV1Url}/branches/${branchName}/rule/wrapUpLineOrder`, { commitMessage, asset: order }).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/lines\\wrapUpLineOrder', method: 'put' },
    ),
    getLineTables: new APIRequest(
      ({ branchName, tagName, lineName }) => axiosAuthClient.get<TableDefinition[]>(`${defaultV1Url}/branches/${branchName}/rule/lines/${lineName}/tables${tagName ? `?tagName=${tagName}` : ''}`).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/rule\\/lines\\/[a-zA-Z0-9\\-]+\\/tables', method: 'get' },
    ),
    getBranchTables: new APIRequest(
      ({ branchName, tagName }) => axiosAuthClient.get<TableDefinition[]>(`${defaultV1Url}/branches/${branchName}/rule/tables${tagName ? `?tagName=${tagName}` : ''}`).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/rule\\/tables', method: 'get' },
    ),
    addTable: new APIRequest(
      ({
        branchName, lineName, sheetAxis, sheet,
      }) => {
        const formData = new FormData();
        formData.append('sheet', sheet);
        formData.append('sheetAxis', sheetAxis || '');
        return axiosAuthClient.put(`${defaultV1Url}/branches/${branchName}/rule/lines/${lineName}/tables`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      },
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/rule\\/lines\\/[a-zA-Z0-9\\-]+\\/tables', method: 'put' },
    ),
    deleteTable: new APIRequest(
      ({ branchName, lineName, name }) => axiosAuthClient.delete<void>(`${defaultV1Url}/branches/${branchName}/rule/lines/${lineName}/tables/${name}`).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/lines\\/[a-zA-Z0-9\\-]+\\/tables\\/[a-zA-Z0-9\\-]+', method: 'delete' },
    ),
    exportTable: new APIRequest(
      ({
        branchName, tagName, lineName, name,
      }) => axiosAuthClient.get(`${defaultV1Url}/branches/${branchName}/rule/lines/${lineName}/tables/${name}${tagName ? `?tagName=${tagName}` : ''}`, { responseType: 'blob' }).then(getFile),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/rule\\/lines\\/[a-zA-Z0-9\\-]+\\/tables\\/[a-zA-Z0-9\\-]+', method: 'get' },
    ),
    getLineTests: new APIRequest(
      ({ branchName, tagName, lineName }) => axiosAuthClient.get<LineTestDefinition[]>(`${defaultV1Url}/branches/${branchName}/rule/lines/${lineName}/tests${tagName ? `?tagName=${tagName}` : ''}`).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/rule\\/lines\\/[a-zA-Z0-9\\-]+\\/tests', method: 'get' },
    ),
    addLineTest: new APIRequest(
      ({
        branchName, lineName, name, sheet,
      }) => {
        const formData = new FormData();
        formData.append('file', sheet);
        // formData.append('forEach', forEach ? 'true' : 'false');
        return axiosAuthClient.put(`${defaultV1Url}/branches/${branchName}/rule/lines/${lineName}/tests/${name}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          responseType: 'blob',
        }).then(({ data }) => data);
      },
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/rule\\/lines\\/[a-zA-Z0-9\\-]+\\/tests', method: 'put' },
    ),
    importForEachTests: new APIRequest(
      ({
        branchName, lineName, name, sheet,
      }) => {
        const formData = new FormData();
        formData.append('file', sheet);
        return axiosAuthClient.put(`${defaultV1Url}/branches/${branchName}/rule/lines/${lineName}/tests/${name}/foreach`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          responseType: 'blob',
        }).then(({ data }) => data);
      },
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/rule\\/lines\\/[a-zA-Z0-9\\-]+\\/tests\\/[a-zA-Z0-9\\-]+\\/foreach', method: 'put' },
    ),
    deleteLineTest: new APIRequest(
      ({ branchName, lineName, name }) => axiosAuthClient.delete<void>(`${defaultV1Url}/branches/${branchName}/rule/lines/${lineName}/tests/${name}`).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/rule\\//lines\\/[a-zA-Z0-9\\-]+\\/tests\\/[a-zA-Z0-9\\-]+', method: 'delete' },
    ),
    exportLineTest: new APIRequest(
      ({
        branchName, tagName, lineName, name,
      }) => axiosAuthClient.get(`${defaultV1Url}/branches/${branchName}/rule/lines/${lineName}/tests/${name}${tagName ? `?tagName=${tagName}` : ''}`, {
        responseType: 'blob',
        headers: {
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
      }).then(getFile),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/rule\\/lines\\/[a-zA-Z0-9\\-]+\\/tests\\/[a-zA-Z0-9\\-]+', method: 'get' },
    ),
    runLineTest: new APIRequest(
      ({ branchName, lineName, name }) => axiosAuthClient.post<void>(`${defaultV1Url}/branches/${branchName}/rule/lines/${lineName}/tests/${name}/run`).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/rule\\/lines\\/tests\\/[a-zA-Z0-9\\-]+\\', method: 'post' },
    ),
    exportTestTemplate: new APIRequest(
      ({
        branchName, tagName, lineName, exportType,
      }) => axiosAuthClient.get(`${defaultV1Url}/branches/${branchName}/rule/lines/${lineName}/tests/template?type=${exportType}${tagName ? `&tagName=${tagName}` : ''}`, { responseType: 'blob' }).then(getFile),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/rule\\/lines\\/[a-zA-Z0-9\\-]+\\/tests\\/template', method: 'get' },
    ),
    validateBranch: new APIRequest(
      ({ branchName }) => axiosAuthClient.get<EditorValidationErrors>(`${defaultV1Url}/branches/${branchName}/rule/validation`).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/rule\\/validation\\/lines\\/[a-zA-Z0-9\\-]+', method: 'get' },
    ),
    validateLine: new APIRequest(
      ({ branchName, lineName, lineDefinition }) => axiosAuthClient.post<EditorValidationErrors>(`${defaultV1Url}/branches/${branchName}/rule/validation/line`, { name: lineName, lineDefinition }).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/rule\\/validation\\/lines\\/[a-zA-Z0-9\\-]+', method: 'get' },
    ),
    typeaheadBucketValue: new APIRequest(
      ({ branchName, bucket, partialValue }) => axiosAuthClient.get<TypeAheadBucketValueReplacements>(`${defaultV1Url}/branches/${branchName}/typeahead/contribution`, { params: { bucket, partialValue } }).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/typeahead\\/contribution', method: 'get' },
    ),
    typeaheadContributionBucket: new APIRequest(
      ({ branchName, expression }) => axiosAuthClient.get<TypeAheadBucketValueReplacements>(`${defaultV1Url}/branches/${branchName}/typeahead/contribution/bucket`, { params: { expression } }).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/typeahead\\/contribution\\/bucket', method: 'get' },
    ),
    editorConfiguration: new APIRequest(
      () => axiosAuthClient.get<EditorConfiguration>(`${defaultV1Url}/configurations/editor`).then(({ data }) => data),
      { urlRegexp: '\\/api\\/configurations\\/editor', method: 'get' },
    ),
  } as EditorAPI;

  

  return api;
};
